<template>
  <div>
    <row-col-text-field
      :value="industrialProperty.title"
      :label="$t('form.industrial_property.title')"
      :rules="rules.char_required_max_250"
      :required="true"
      :readonly="readonly"
      @input="update('title', $event)"
    />

    <h5 class="mb-2">{{ $t("form.industrial_property.type") }}</h5>
    <v-btn-toggle
      :value="industrialProperty.property_type"
      mandatory
      dense
      class="industrial-property-type mb-5"
      @change="update('property_type', $event)"
    >
    <v-btn
      v-for="type in propertyTypes"
        :key="type.value"
        :value="type.value"
        :disabled="readonly"
        text
      >
        {{ type.text }}
      </v-btn>
    </v-btn-toggle>

    <MultilingualTextField
      :value="industrialProperty.description"
      :label="$t('form.industrial_property.description')"
      :readonly="readonly"
      :rich="true"
      entity-type="industrial_property"
      field="description"
      @input="update('description', $event)"
    />
    <p class="text--secondary text-caption mt-1 mb-4">
      {{ $t("form.industrial_property.description_hint") }}
    </p>

    <v-row>
      <v-col cols="12" md="4" class="py-0">
        <row-col-text-field
          :value="industrialProperty.application_number"
          :label="$t('form.industrial_property.application_number')"
          :rules="rules.char_max_120"
          :readonly="readonly"
          @input="update('application_number', $event)"
        />
      </v-col>
      <col-date-field
        :value="industrialProperty.application_date"
        :label="$t('form.industrial_property.application_date')"
        :readonly="readonly"
        @input="update('application_date', $event)"
      />
      <v-col cols="12" md="4" class="py-0">
        <h5>{{ $t("form.industrial_property.usc_percentage") }}</h5>
        <v-text-field
          :value="industrialProperty.usc_percentage"
          type="number"
          min="0"
          max="100"
          step="0.01"
          suffix="%"
          :rules="percentageRules"
          :readonly="readonly"
          :disabled="readonly"
          outlined
          @input="update('usc_percentage', $event)"
        />
      </v-col>
    </v-row>

    <template v-if="industrialProperty.property_type === 'patent'">
      <v-divider class="mt-5 mb-5" />
      <h5 class="mb-3">{{ $t("form.industrial_property.patent_information") }}</h5>
      <row-col-text-field
        :value="industrialProperty.parent_patent_title"
        :label="$t('form.industrial_property.parent_patent_title')"
        :rules="rules.char_max_250"
        :readonly="readonly"
        @input="update('parent_patent_title', $event)"
      />
      <v-row>
        <v-col cols="12" md="6" class="py-0">
          <row-col-text-field
            :value="industrialProperty.publication_number"
            :label="$t('form.industrial_property.publication_number')"
            :rules="rules.char_max_120"
            :readonly="readonly"
            @input="update('publication_number', $event)"
          />
        </v-col>
        <col-date-field
          :value="industrialProperty.publication_date"
          :label="$t('form.industrial_property.publication_date')"
          :readonly="readonly"
          @input="update('publication_date', $event)"
        />
      </v-row>
      <v-row>
        <col-date-field
          :value="industrialProperty.grant_date"
          :label="$t('form.industrial_property.grant_date')"
          :readonly="readonly"
          @input="update('grant_date', $event)"
        />
        <v-col cols="12" md="4" class="py-0">
          <row-col-text-field
            :value="industrialProperty.country_code"
            :label="$t('form.industrial_property.country_code')"
            :rules="rules.char_max_10"
            :readonly="readonly"
            @input="update('country_code', $event)"
          />
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <row-col-text-field
            :value="industrialProperty.patent_code"
            :label="$t('form.industrial_property.patent_code')"
            :rules="rules.char_max_120"
            :readonly="readonly"
            @input="update('patent_code', $event)"
          />
        </v-col>
      </v-row>
      <h5>{{ $t("form.industrial_property.patent_status") }}</h5>
      <v-select
        :value="industrialProperty.patent_status"
        :items="patentStatuses"
        item-text="text"
        item-value="value"
        :readonly="readonly"
        :disabled="readonly"
        outlined
        @input="update('patent_status', $event)"
      />
      <row-col-text-field
        :value="industrialProperty.patent_url"
        :label="$t('form.industrial_property.patent_url')"
        :rules="rules.char_max_500"
        :readonly="readonly"
        @input="update('patent_url', $event)"
      />
    </template>

    <template v-else-if="industrialProperty.property_type === 'semiconductor_topography'">
      <v-divider class="mt-5 mb-5" />
      <h5 class="mb-3">{{ $t("form.industrial_property.topography_information") }}</h5>
      <v-row>
        <col-date-field
          :value="industrialProperty.grant_date"
          :label="$t('form.industrial_property.grant_date')"
          :readonly="readonly"
          @input="update('grant_date', $event)"
        />
      </v-row>
    </template>

    <v-divider class="mt-6 mb-5" />
    <h5>{{ $t("form.industrial_property.exploitation_contracts") }}</h5>
    <row-col-select-multiple-field
      :value="industrialProperty.exploitation_organizations"
      data-source="organization"
      :label="$t('form.industrial_property.exploitation_organizations')"
      :readonly="readonly"
      @input="update('exploitation_organizations', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5>{{ $t("form.industrial_property.authorship") }}</h5>
    <row-col-select-multiple-field
      :value="industrialProperty.authors"
      data-source="person"
      :label="$t('form.industrial_property.authors')"
      :required="true"
      :readonly="readonly"
      @input="update('authors', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5>{{ $t("form.industrial_property.holders") }}</h5>
    <row-col-select-multiple-field
      :value="industrialProperty.internal_holders"
      data-source="person"
      :label="$t('form.industrial_property.internal_holders')"
      :readonly="readonly"
      @input="update('internal_holders', $event)"
    />
    <div class="mb-4">
      <h5 class="mb-2">{{ $t("form.industrial_property.external_holders") }}</h5>
      <ArrayFieldForm
        v-if="!readonly"
        :value="industrialProperty.external_holders"
        name-field="external_holders"
        :label="$t('form.industrial_property.external_holder')"
        @input="update('external_holders', $event)"
      />
      <v-text-field v-else :value="externalHoldersText" disabled />
    </div>
    <row-col-select-multiple-field
      :value="industrialProperty.holder_organizations"
      data-source="organization"
      :label="$t('form.industrial_property.holder_organizations')"
      :readonly="readonly"
      @input="update('holder_organizations', $event)"
    />

    <v-divider class="mt-6 mb-5" />
    <h5 class="mb-3">{{ $t("form.industrial_property.related_records") }}</h5>
    <row-col-select-multiple-field
      :value="industrialProperty.softwares"
      data-source="software"
      :label="$t('form.industrial_property.related_softwares')"
      :readonly="readonly"
      @input="update('softwares', $event)"
    />
    <row-col-select-multiple-field
      :value="industrialProperty.publications"
      data-source="publication"
      :label="$t('form.industrial_property.related_publications')"
      :readonly="readonly"
      @input="update('publications', $event)"
    />
    <row-col-select-multiple-field
      :value="industrialProperty.projects"
      data-source="project"
      :label="$t('form.industrial_property.related_projects')"
      :readonly="readonly"
      @input="update('projects', $event)"
    />
  </div>
</template>

<script>
import validationRules from "@/mixins/validationRules";
import ArrayFieldForm from "@/components/reusables/ArrayFieldForm.vue";
import ColDateField from "@/components/reusables/ColDateField.vue";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import RowColSelectMultipleField from "@/components/reusables/RowColSelectMultipleField.vue";
import RowColTextField from "@/components/reusables/RowColTextField.vue";

export default {
  name: "IndustrialPropertyFields",
  mixins: [validationRules],
  components: {
    ArrayFieldForm,
    ColDateField,
    MultilingualTextField,
    RowColSelectMultipleField,
    RowColTextField
  },
  props: {
    industrialProperty: { type: Object, required: true },
    readonly: { type: Boolean, default: false }
  },
  computed: {
    propertyTypes() {
      return ["patent", "software_registration", "semiconductor_topography"].map(value => ({
        value,
        text: this.$t(`form.industrial_property.types.${value}`)
      }));
    },
    patentStatuses() {
      return ["pending", "granted", "published", "denied"].map(value => ({
        value,
        text: this.$t(`form.industrial_property.patent_statuses.${value}`)
      }));
    },
    percentageRules() {
      return [
        value => value !== "" && value !== null || this.$t("forms.field.validation.required"),
        value => Number(value) >= 0 && Number(value) <= 100 || this.$t("form.industrial_property.percentage_error")
      ];
    },
    externalHoldersText() {
      const holders = (this.industrialProperty.external_holders || []).filter(Boolean);
      return holders.length ? holders.join(", ") : "-";
    }
  },
  methods: {
    update(field, value) {
      this.$emit("update", field, value);
    }
  }
};
</script>

<style scoped>
.industrial-property-type {
  max-width: 100%;
  flex-wrap: wrap;
}

.industrial-property-type .v-btn {
  margin: 0;
}
</style>
